import React, { useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import {
	Button,
	GridContainer,
	Input,
	Modal,
	Select,
	SliderCheckbox,
	SpinnerOverlay,
	validationErrMessages,
} from '@components/common';
import Confirm from '@components/common/Confirm';
import SwapUserReferenceForm from '@components/ModalComponents/SwapUserReferenceForm';
import { useEmailValidation } from '@components/UsersTable/UserMultipleOrganisationsForm/useEmailValidation';

import { useQueryRoles } from '@hooks/roles/useQueryRoles';
import useMutateCheckEmail from '@hooks/useMutateCheckEmail';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { isEmpty } from '@utils/common';
import { generateRolesForCreateUser, getRoleSelectionHelper } from '@utils/rolesUtils';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IUserFormValues } from '@interfaces/components/form';
import { IUserEntity, TRole } from '@interfaces/user/user';

import { EUserRoles, TOrganizationType } from '@enums/common';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480, MODAL_WIDTH_580 } from '@constants/modals';
import { regExpLetters } from '@constants/regExp';
import { adminRoles, EUserFormFields, userFormAreas } from '@components/ModalComponents/UserForm/constants';

import { baseSchema } from './schema';

import styles from './UserForm.module.scss';

const ASSET_OWNER_CONTACT = 'ASSET_OWNER_CONTACT';

export interface IUserFormProps {
	organisationTypes?: Array<TOrganizationType>;
	closeModals?: () => void;
	editableUser?: IUserEntity;
	isEditMode: boolean;
	isViewMode: boolean;
	isLoading?: boolean;
	organizationId?: string;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	submitForm: (values: IUserFormValues) => void;
	toggleCloseConfirmModal?: () => void;
}

const UserForm = ({
	closeModals,
	editableUser,
	organizationId,
	isEditMode,
	isViewMode,
	isLoading,
	organisationTypes,
	setIsEditMode,
	setIsDirtyFormModal,
	submitForm,
	toggleCloseConfirmModal = () => null,
}: IUserFormProps) => {
	const {
		accessPermission: {
			assetOwnerOrganizationAdministrator,
			assetConsumerOrganizationAdministrator,
			networkOperatorOrganizationAdministrator,
		},
	} = useAppSelector(auth);

	const { id } = useParams();
	const adminRoleNames = adminRoles.map(({ role }) => role);

	const isAdmin = editableUser?.roles?.some((item) => adminRoleNames.includes(item.name));
	const isLoggedInUserOrganizationAdministrator =
		assetOwnerOrganizationAdministrator ||
		assetConsumerOrganizationAdministrator ||
		networkOperatorOrganizationAdministrator;

	const isFieldsDisabled = isViewMode && !isEditMode;
	const isEmailReadOnly = isEditMode && isLoggedInUserOrganizationAdministrator;
	const isExistUserAdmin = editableUser ? (!isAdmin || !editableUser.isLastAdmin) : true;
	const isNegativeButton = isViewMode || isEditMode;

	const currentOrganisationTypes = isNegativeButton
		? editableUser?.organization?.types?.filter((type: TSelectOption<TOrganizationType>) => !!type?.name)
			.map((type) => type.name)
		: organisationTypes;

	const [isRequiredJobTitle, setIsRequiredJobTitle] = useState(false);
	const [isSwapUserModal, setIsSwapUserModal] = useState(false);
	const [isDirtySwapUserForm, setIsDirtySwapUserForm] = useState(false);
	const [isWarningModal, setIsWarningModal] = useState(false);

	const [allRoles, setAllRoles] = useState<any>([]);

	const { roles: rolesToCreate, isLoadingRoles } = useQueryRoles({
		organizationId: id || editableUser?.organization?.id,
		isExistUserAdmin,
	});

	useEffect(() => {
		if (!rolesToCreate.length) return;

		const generatedRoles = generateRolesForCreateUser({
			editableUser,
			rolesToCreate,
			currentOrganisationTypes,
			isExistUserAdmin,
		});

		setAllRoles(generatedRoles);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rolesToCreate, isExistUserAdmin, editableUser]);

	const {
		checkEmailError,
		emailStatus: {
			alreadyExists = false,
			isInAnotherOrganization = false,
			isEmptyOrganizations = false,
		} = {},
		checkEmail,
	} = useMutateCheckEmail();

	const { validateEmail } = useEmailValidation({
		organizationId,
		defaultValue: editableUser?.email,
		checkEmailFn: checkEmail,
	});

	const schema = baseSchema(
		validateEmail,
		checkEmailError,
		isRequiredJobTitle,
	);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		clearErrors,
		setError,
		control,
		trigger,
	} = useForm<FieldValues>({
		mode: EValidationMode.onChange,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		shouldUnregister: true,
		defaultValues: {
			[EUserFormFields.firstName]: editableUser?.firstName || '',
			[EUserFormFields.lastName]: editableUser?.lastName || '',
			[EUserFormFields.email]: editableUser?.email || '',
			[EUserFormFields.phoneNumber]: editableUser?.phoneNumber || '',
			[EUserFormFields.jobTitle]: editableUser?.jobTitle || '',
			[EUserFormFields.is2FA]: (isNegativeButton && editableUser?.is2FA !== null)
				? editableUser?.is2FA
				: false,
			[EUserFormFields.roles]: null,
		},
	});

	const selectedRoles = watch(EUserFormFields.roles);
	const jobTitle = watch(EUserFormFields.jobTitle);
	const phoneNumber = watch(EUserFormFields.phoneNumber) ? `+${watch(EUserFormFields.phoneNumber)}` : '';
	const roles = watch(EUserFormFields.roles);
	const firstName = watch(EUserFormFields.firstName);
	const lastName = watch(EUserFormFields.lastName);

	const isUniqueOrInAnotherOrganisation = !alreadyExists
		|| isInAnotherOrganization
		|| (!isInAnotherOrganization && isEmptyOrganizations);

	const isSubmitDisabled = !roles || !firstName || !lastName || !isUniqueOrInAnotherOrganisation;

	const toggleSwapUserReferenceModal = () => setIsSwapUserModal(!isSwapUserModal);
	const toggleWarningModal = () => setIsWarningModal(!isWarningModal);

	const handleEmailPaste = () => {
		requestAnimationFrame(() => {
			void trigger(EUserFormFields.email);
		});
	};

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	useEffect(() => {
		if (allRoles?.length) {
			setValue(EUserFormFields.roles, isNegativeButton
				? allRoles.filter((roleItem: TSelectOption) =>
					editableUser?.roles?.some((element: TRole) =>
						roleItem?.name === element.name))
				: (isExistUserAdmin && !isEditMode)
					? allRoles.filter((roleItem: TSelectOption) => roleItem?.isFixed)
					: null,
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditMode, isViewMode, allRoles]);

	const negativeHandler = () => {
		if (isFieldsDisabled) {
			setIsEditMode(true);

			return;
		}

		toggleCloseConfirmModal?.();
	};

	const positiveHandler = () => {
		if (isFieldsDisabled) {
			closeModals?.();

			return;
		}

		void handleSubmit(onSubmit)();
	};

	const onSubmit = (values: IUserFormValues) => {
		if (alreadyExists) {
			toggleWarningModal();
			setEmailUniqueError();

			return;
		}

		const roles = values.roles.map((role: TSelectOption) => role?.name);

		submitForm({ ...values, roles, id: isEditMode ? editableUser?.id : id });
	};

	const setEmailUniqueError = useCallback(() => {
		return setError(EUserFormFields.email, {
			type: 'manual',
			message: validationErrMessages.emailUnique,
		});
	}, [setError]);

	useEffect(() => {
		if (!isUniqueOrInAnotherOrganisation) {
			setEmailUniqueError();
		}
	}, [isUniqueOrInAnotherOrganisation, setEmailUniqueError]);

	const onSelectChange = (options: Array<TSelectOption<string>>) => {
		if (isEmpty(options)) {
			setValue(EUserFormFields.roles, null, { shouldValidate: true });
		} else {
			const {
				isSomeAdminRolesRequired,
				getRolesWithRelevantAdmins,
				getRolesWithoutRelevantAdmins,
				// eslint-disable-next-line react-hooks/rules-of-hooks
			} = getRoleSelectionHelper({
				multiSelectOptions: options,
				allRoles,
				currentOrganisationTypes,
			});

			if (isSomeAdminRolesRequired) {
				const isPartialRelevantAdminsSelected = roles?.some((role: TSelectOption) =>
					role?.value === EUserRoles.assetOwnerOrganizationAdministrator
					|| role?.value === EUserRoles.networkOperatorOrganizationAdministrator
					|| role?.value === EUserRoles.assetConsumerOrganizationAdministrator) || false;

				if (isPartialRelevantAdminsSelected) {
					setValue(EUserFormFields.roles, getRolesWithoutRelevantAdmins());
				} else {
					setValue(EUserFormFields.roles, getRolesWithRelevantAdmins());
				}
			} else {
				setValue(EUserFormFields.roles, options);
			}

			clearErrors(EUserFormFields.roles);
		}
	};

	useEffect(() => {
		const isExistRole = !!(selectedRoles
			?.some((roleItem: TSelectOption) => roleItem?.name === ASSET_OWNER_CONTACT));

		clearErrors('jobTitle');
		setIsRequiredJobTitle(isExistRole);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRoles, jobTitle]);

	const onChangePhoneNumber = (phone: string) => {
		const formattedPhone = phone.slice(1);

		setValue('phoneNumber', formattedPhone);
	};

	const closeSwapUserModal = () => {
		toggleSwapUserReferenceModal();

		if (closeModals) closeModals();
	};

	return (
		<>
			<GridContainer
				columns={2}
				customClassName={styles.userFormContainer}
				gap='15px 20px'
				gridTemplateAreas={userFormAreas}
			>
				{(isLoading || isLoadingRoles) && <SpinnerOverlay/>}
				<div className={styles.firstName}>
					<Input
						disabled={isFieldsDisabled}
						errorMessage={errors?.firstName?.message}
						label='First Name'
						name={EUserFormFields.firstName}
						placeholder='Enter first name'
						regexValidation={regExpLetters}
						register={register}
						required
						setValue={setValue}
						withCustomValidation
					/>
				</div>
				<div className={styles.lastName}>
					<Input
						disabled={isFieldsDisabled}
						errorMessage={errors?.lastName?.message}
						label='Last Name'
						name={EUserFormFields.lastName}
						placeholder='Enter last name'
						regexValidation={regExpLetters}
						register={register}
						required
						setValue={setValue}
						withCustomValidation
					/>
				</div>
				<div className={styles.email}>
					<Input
						disabled={isFieldsDisabled}
						errorMessage={errors?.email?.message}
						isReadOnly={isEmailReadOnly}
						label='Email Address'
						name={EUserFormFields.email}
						onPaste={handleEmailPaste}
						placeholder='Enter email address'
						register={register}
						required
					/>
				</div>
				<div className={styles.contactNumber}>
					<Input
						disabled={isFieldsDisabled}
						errorMessage={errors?.phoneNumber?.message}
						isPhoneNumber
						label='Contact Number'
						name={EUserFormFields.phoneNumber}
						onChange={onChangePhoneNumber}
						placeholder='Enter contact number'
						value={phoneNumber}
					/>
				</div>
				<div className={styles.role}>
					<Select
						closeMenuOnSelect={false}
						control={control}
						disabled={isFieldsDisabled}
						errorMessage={(errors.roles as any)?.message}
						handleChange={onSelectChange}
						isClearable
						isError={!!errors?.roles}
						isMulti
						label='Role'
						name={EUserFormFields.roles}
						options={allRoles}
						placeholder='Select roles'
						required
						value={roles}
						withFixedValue
					/>
				</div>
				<div className={styles.job}>
					<Input
						disabled={isFieldsDisabled}
						errorMessage={errors?.jobTitle?.message}
						label='Job Title'
						name={EUserFormFields.jobTitle}
						placeholder='Enter job title'
						register={register}
						required={isRequiredJobTitle}
					/>
				</div>
				<div className={styles.fa}>
					<SliderCheckbox
						disabled
						id='is2FA'
						isLabelAfter
						label='Enable Two-Factor Authentication (2FA)'
						name={EUserFormFields.is2FA}
						register={register}
					/>
					{isEditMode && !editableUser?.isBlocked && (
						<div
							className={styles.swapReference}
							onClick={toggleSwapUserReferenceModal}
						>
							Swap user reference
						</div>
					)}
				</div>
				<div className={styles.submitButtons}>
					<Button
						onClick={negativeHandler}
						text={isFieldsDisabled ? 'Edit user' :'Cancel'}
						withoutBackground
					/>
					<Button
						disabled={isSubmitDisabled}
						onClick={positiveHandler}
						text={isFieldsDisabled
							? 'Close'
							: 'Save'
						}
					/>
				</div>
			</GridContainer>
				<Modal
					isConfirmDialogNeededOnClose
					isOpen={isWarningModal}
					maxWidth={MODAL_WIDTH_480}
					onClose={toggleWarningModal}
					title='Warning'
					width='auto'
				>
					<Confirm negativeBtnText='Close'>
						The user with the provided email already exists on the platform.
						<br/>
						Please contact the Platform Owner for assistance.
					</Confirm>
				</Modal>
			{editableUser && (
				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtySwapUserForm}
					isOpen={isSwapUserModal}
					minWidth={MODAL_WIDTH_580}
					onClose={toggleSwapUserReferenceModal}
					title='Swap user reference'
					width='auto'
				>
					<SwapUserReferenceForm
						closeForm={closeSwapUserModal}
						currentUser={editableUser}
						organisationId={id || organizationId}
						setIsDirtyForm={setIsDirtySwapUserForm}
					/>
				</Modal>
			)}
		</>
	);
};

export default UserForm;
